import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { config } from '@uhg-abyss/web/tools/config';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeSearchFilterEvent } from '../../common/AdobeTagging/adobeSearchFilterEvent';
import { adobeSearchResultClickEvent } from '../../common/AdobeTagging/adobeSearchResultClickEvent';
import {
  BrowseAZ,
  DisableableChipObject,
} from '../../common/BrowseAZ/BrowseAZ';
import { Constants, DEFAULT_EMPTY_ARRAY } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { useUserLocation } from '../../hooks/useUserLocation';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { removeAccents } from '../../utils/string.utils';
import {
  IsExternallink,
  filterForLevelFundedPlans,
  getGuestResultsPageName,
  getLobForPortalData,
  handleGuestPlanNavigation,
  setGuestPlanSelectionData,
} from './Guest.utils';
import {
  ContainerForMultiplePlans,
  ContainerForPlanSelection,
  ContainerPlanNamesFlex,
  PlanInputContainer,
  PlanNameLink,
  SearchInputContainer,
  StyledCategoryContainer,
  StyledContainerforPage,
  StyledDropdownMenu,
  StyledFundedHealthButton,
  StyledListOfCategoryContainer,
  StyledPlanSearch,
  StyledText,
  StyledTextForNoResults,
} from './GuestMultiplePlan.style';
import { GuestLocation } from './selectLocation/GuestLocation.utils';
import { isDualYearCheckEnabledForGuest } from './utils';

const FPC_BASE_URL = config('FPC_BASE_URL');

enum SORT_ORDER {
  asc,
  desc,
}

export const GuestMultiplePlan = ({
  selectedLocation,
}: {
  selectedLocation: GuestLocation;
}) => {
  const {
    stateCode,
    zipCode: zipcode,
    longitude,
    latitude,
  } = useGeoLocationStorage();

  const [searchText, setSearchText] = useState('');
  const [selectedChip, setSelectedChip] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<SORT_ORDER>(SORT_ORDER.asc);
  const [isLevelFundedSelected, setIsLevelFundedSelected] = useState(false);
  const [allSearchedData, setAllSearchedData] = useState([]);
  const [itemsWithCategoryName, setItemsWithCategoryName] = useState([]);
  const [itemsWithoutCategoryName, setItemsWithoutCategoryName] = useState([]);
  const [letterMapping, setLetterMapping] = useState({});
  const [filteredLetterMapping, setFilteredLetterMapping] = useState({});

  const featureFlags = useLagoon('feature-flags')();
  const planSelectionConfig = useLagoon('config')();
  const lagoonUIMessage = useLagoon('ui-messaging')();

  const sortOrderUpdated = useRef(false);
  const isInitialMount = useRef(true);
  const searchTextRef = useRef({ searchText });

  const form = useForm({
    defaultValues: {
      PLAN_SEARCH: '',
    },
  });

  const { navigate, getLocation } = useRouter();
  const { state } = getLocation();
  const plansFromState = state?.availablePlans || DEFAULT_EMPTY_ARRAY;
  const [, getFpcGuestToken] = useFpcGuestToken({});
  const { toast } = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [userLocation, getUserLocation] = useUserLocation({});

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );

  const {
    planName,
    policyNumber,
    pvrcode,
    clusterKey: clusterKeyFromDeeplink,
  } = guestPlanSelectorData || {};

  useEffect(() => {
    if (plansFromState.length > 0) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(plansFromState)
      );
    }
  }, [plansFromState]);

  const storedPlans = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
  );
  const availablePlanNames = useMemo(
    () => (storedPlans ? JSON.parse(storedPlans) : plansFromState),
    [storedPlans, plansFromState]
  );

  const lob = searchParams?.get('planSelectionLob') || '';

  const noPlansInSelectedArea = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NO_PLANS_IN_SELECTED_AREA,
  })?.message;
  const enterPlanName = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.ENTER_A_PLAN_NAME,
  })?.message;
  const planNameLabel = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_NAME,
  })?.message;
  const clear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CLEAR,
  })?.message;
  const levelFundHealthPlans = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LEVEL_FUNDED_HEALTH_PLANS,
  })?.message;
  const plansAZ = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLANS_A_Z,
  })?.message;
  const plansZA = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLANS_Z_A,
  })?.message;
  const noMatchingPlans = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NO_MATCHING_PLANS,
  })?.message;

  const toastMessageForFpcTokenError = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.FPC_ERROR_POPUP_MESSAGE,
  })?.message;

  const getLetterMapping = (planNames) => {
    const letterMap: any = {};

    // Rename the last element in Constants.BROWSE_AZ
    const lastIndex = Constants.BROWSE_AZ.length - 1;
    if (lastIndex >= 0) {
      Constants.BROWSE_AZ[lastIndex].label = clear;
      Constants.BROWSE_AZ[lastIndex].value = 'Clear';
    }

    Constants.BROWSE_AZ.forEach((letter) => {
      letterMap[letter.label] = [];
    });
    planNames?.forEach((record) => {
      if (record?.planCategory) {
        // Use planCategory if available
        const startingLetter = removeAccents(
          record.planCategory.substring(0, 1)
        );
        letterMap[startingLetter]?.push(record);
      } else if (record?.planDetails) {
        const planDetails = Array.isArray(record.planDetails)
          ? record.planDetails
          : [record.planDetails];

        planDetails.forEach((plan: any) => {
          const startingLetter = removeAccents(plan?.planName?.substring(0, 1));
          letterMap[startingLetter]?.push({
            planCategory: record.planCategory || null, // Use planCategory if available, otherwise null
            planDetails: [plan], // Wrap the plan in an array
          });
        });
      }
    });
    return letterMap;
  };

  // Initial page render
  useEffect(() => {
    adobePageTrackEvent();

    const letterMap = getLetterMapping(availablePlanNames);

    setLetterMapping(letterMap);
  }, [availablePlanNames]);

  const chipsWithDisabled = useMemo(
    () =>
      Constants.BROWSE_AZ.slice(1).map((letter: DisableableChipObject) => ({
        ...letter,
        isDisabled:
          filteredLetterMapping[letter.value]?.length === 0 &&
          letter.value !== 'Clear',
      })),
    [filteredLetterMapping]
  );

  const sortCategories = (order: SORT_ORDER) => {
    const categoriesToSort = selectedChip
      ? letterMapping[selectedChip] || [] // Use the categories for the selected chip
      : availablePlanNames;

    return [...categoriesToSort].sort((a, b) => {
      // Get the name for sorting
      const nameA =
        a?.planCategory ||
        (Array.isArray(a?.planDetails) && a.planDetails[0]?.planName) ||
        '';
      const nameB =
        b?.planCategory ||
        (Array.isArray(b?.planDetails) && b.planDetails[0]?.planName) ||
        '';

      // Compare names based on the sort order
      return order === SORT_ORDER.asc
        ? nameA.localeCompare(nameB) // A to Z
        : nameB.localeCompare(nameA); // Z to A
    });
  };

  const handleSortToggle = (order: SORT_ORDER) => {
    sortOrderUpdated.current = true;
    setSortOrder(order);
  };

  // Re-filter list of plans whenever a filter is changed
  useEffect(() => {
    // Order categories based on sort order
    const sortedCategories = sortCategories(sortOrder);

    // Filter categories based on search text
    const textFilteredCategories = sortedCategories?.filter((item) => {
      const planCategory = item?.planCategory || '';
      const planDetails = item?.planDetails || [];
      const search = searchText?.trim()?.toLocaleLowerCase();

      const categoryNameMatch = planCategory
        .toLocaleLowerCase()
        .includes(search);

      const planDetailsMatch = planDetails.some((plan) =>
        plan?.planName.toLocaleLowerCase().includes(search)
      );

      return categoryNameMatch || planDetailsMatch;
    });

    let filteredCategories;

    // Filter categories if level funded filter is selected
    if (isLevelFundedSelected) {
      filteredCategories = filterForLevelFundedPlans(textFilteredCategories);
    } else {
      filteredCategories = textFilteredCategories;
    }

    setAllSearchedData(filteredCategories || []);
    setFilteredLetterMapping(getLetterMapping(filteredCategories || []));
  }, [
    sortOrder,
    searchText,
    selectedChip,
    isLevelFundedSelected,
    availablePlanNames,
  ]);

  useEffect(() => {
    const withCategory = allSearchedData?.filter((item) => item.planCategory);

    const withoutCategory = allSearchedData
      ?.filter((item) => !item.planCategory) // Only items without a category name
      ?.map((item) => ({
        ...item,
        planDetails: item.planDetails || [],
      }));

    setItemsWithCategoryName(withCategory || []);
    setItemsWithoutCategoryName(withoutCategory || []);
  }, [allSearchedData]);

  useEffect(() => {
    const previous = searchTextRef.current;

    if (
      previous.searchText.length === 0 &&
      searchText.length === 1 &&
      selectedChip !== null
    ) {
      setSelectedChip('');
    }
    return () => {
      previous.searchText = searchText;
    };
  }, [searchText]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      adobeSearchFilterEvent({
        isGuest: true,
        filters: getFilters(),
        term: selectedLocation.label || '',
        type: 'site',
      });
    }
  }, [searchText, selectedChip, sortOrder]);

  const selectedChipWithClear = (chip: string) => {
    if (chip === 'Clear') {
      const button = Array.from(document.querySelectorAll('button')).find(
        (btn) => btn.textContent.trim() === 'close'
      );

      if (button) {
        button.click();
      }
      setSelectedChip('');
      setSearchText('');
      setIsLevelFundedSelected(false);
      form?.setValue(Constants.INPUT_MODELS.PLAN_SEARCH, '');
      form?.clearErrors(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH');
    } else {
      setSelectedChip(chip);
    }
  };

  const coverageType = searchParams?.get('coverageType') || '';

  const getFilters = () => {
    const filters = [searchText, selectedChip];
    if (sortOrderUpdated.current) {
      filters.push(sortOrder === SORT_ORDER.asc ? 'Plans A-Z' : 'Plans Z-A');
    }
    return filters.filter(Boolean).join('|');
  };

  const routeToPsxOrDualYearSelection = (
    selectedPlan: any,
    planIndex: number,
    planCategory?: string
  ) => {
    const chipValue = searchParams?.get('chipValue') || '';
    const fipscode =
      stateCode || sessionStorage.getItem('countyFipsCode') || '';
    const signInUrl = planSelectionConfig?.find(
      (row) => row.key === `SIGNIN_URL_${lob.toUpperCase()}`
    )?.value;

    const isDualYearCheckEnabled = isDualYearCheckEnabledForGuest(
      lob,
      featureFlags,
      selectedPlan
    );
    adobeSearchResultClickEvent({
      filters: getFilters(),
      isGuest: true,
      linkName: selectedPlan.planName,
      linkPosition: planIndex + 1,
      term: selectedLocation.label || '',
      type: 'site',
    });

    if (isDualYearCheckEnabled) {
      setGuestPlanSelectionData({
        selectedPlan,
        lob,
        coverageType,
        stateCode,
        planName,
        policyNumber,
        allConfigs: planSelectionConfig,
        pvrcode,
        clusterKeyFromDeeplink,
      });
      navigate(
        `/plan-year-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`
      );
      return;
    }
    const isGroupDrivenEnabled = getFeatureFlag(
      featureFlags || [],
      ConstantsLagoon.GUEST_PLAN_SELECTION.ENABLE_GROUP_DRIVEN
    );

    handleGuestPlanNavigation({
      getFpcGuestToken,
      selectedPlan,
      planYear: selectedPlan?.years?.[0].planYear,
      fipscode,
      FPC_BASE_URL: FPC_BASE_URL || '',
      zipcode,
      chipValue,
      coverageType,
      navigate,
      lob,
      stateCode,
      longitude,
      latitude,
      signInUrl,
      toast,
      toastMessageForFpcTokenError,
      planCategory,
      guestPlanSelectorStoreData: guestPlanSelectorData,
      isGroupDrivenEnabled,
      allConfigs: planSelectionConfig,
      getUserLocation,
      userLocation,
    });
  };

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    guestLob: getLobForPortalData(lob),
    isGuest: true,
    pageName: getGuestResultsPageName(coverageType),
    sitesectionLevel1: 'guest plan selection',
    sitesectionLevel2: 'select coverage type',
  });

  const onInputClear = () => {
    form?.clearErrors(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH');
    setSearchText('');
  };

  const onInputChange = (inputValue) => {
    form?.setValue(Constants.INPUT_MODELS.PLAN_SEARCH, inputValue, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setSearchText(
      form?.getValues(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH')
    );
  };

  const handleLevelFundedClick = () => {
    setIsLevelFundedSelected(!isLevelFundedSelected);
  };

  const isGuestEni = Boolean(lob === 'EI');

  return (
    <PageBody>
      <ContainerForMultiplePlans>
        <SearchInputContainer>
          <PlanInputContainer>
            <FormProvider state={form}>
              <StyledPlanSearch
                data-auto-testid="search-plan-input-box"
                data-testid="search-plan-input-box"
                enableOutsideScroll
                isClearable
                isLoading={false}
                keys={['value', 'plan']}
                label={planNameLabel}
                model={Constants.INPUT_MODELS.PLAN_SEARCH}
                onClear={onInputClear}
                onInputChange={onInputChange}
                openOnFocus={false}
                placeholder={enterPlanName || 'Enter a plan name'}
                searchButtonVariant="outlined"
                validators={{
                  validate: {
                    hasValue: (val) => {
                      let errorMessage;
                      if (
                        !Constants.INPUT_MODELS.SPECIALITY_SEARCH_REGEX_PATTERN.test(
                          val
                        )
                      ) {
                        errorMessage = t(
                          'INPUT_MODELS.SPECIALITY_SEARCH_ERROR_DESCRIPTOR'
                        );
                      }
                      if (!val) {
                        form.clearErrors(
                          Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH'
                        );
                        return null;
                      }
                      return errorMessage;
                    },
                  },
                }}
                value={searchText}
              />
            </FormProvider>
          </PlanInputContainer>
        </SearchInputContainer>

        <StyledContainerforPage>
          <BrowseAZ
            chips={chipsWithDisabled}
            guestUser={true}
            selectedChip={selectedChip}
            setSelectedChip={selectedChipWithClear}
          />

          <div style={{ borderTop: '1px solid #E5E5E6' }} />
          {availablePlanNames?.length === 0 ? (
            <Flex
              css={{ marginTop: '40px' }}
              data-testid="no-plans-in-selected-area"
              justify="center"
            >
              <Text color="$red" css={StyledTextForNoResults}>
                {noPlansInSelectedArea}
              </Text>
            </Flex>
          ) : (
            <>
              <div style={{ padding: '24px 0' }}>
                <Flex
                  css={{
                    gap: '24px',
                    maxWidth: '485px',
                  }}
                >
                  {isGuestEni && (
                    <Flex css={{ gap: '4px' }} direction="column">
                      <fieldset
                        style={{ border: 'none', margin: 0, padding: 0 }}
                      >
                        <legend>
                          <Text css={StyledText}>{`${t(
                            'FILTER_LABELS.FILTER_BY'
                          )}:`}</Text>
                        </legend>
                        <StyledFundedHealthButton
                          data-testid="level-funded-health-plans-button"
                          onClick={handleLevelFundedClick}
                          variant={isLevelFundedSelected ? 'solid' : 'outline'}
                        >
                          {levelFundHealthPlans}
                        </StyledFundedHealthButton>
                      </fieldset>
                    </Flex>
                  )}
                  <Flex css={{ gap: '4px' }} direction="column">
                    <Text
                      css={{
                        fontSize: '14px',
                        lineHeight: '18px',
                        fontWeight: 500,
                      }}
                    >{`${t('FILTER_LABELS.SORT_BY')}:`}</Text>
                    <StyledDropdownMenu
                      before={
                        <IconSymbol
                          css={{ color: '#000000 !important' }}
                          icon="swap_vert"
                        />
                      }
                      data-testid="sort-by-drop-down"
                      label={sortOrder === SORT_ORDER.asc ? plansAZ : plansZA}
                      menuItems={[
                        {
                          title: plansAZ,
                          onClick: () => handleSortToggle(SORT_ORDER.asc),
                        },
                        {
                          title: plansZA,
                          onClick: () => handleSortToggle(SORT_ORDER.desc),
                        },
                      ]}
                      outline
                    />
                  </Flex>
                </Flex>

                {allSearchedData?.length > 0 ? (
                  <ContainerForPlanSelection>
                    {itemsWithCategoryName?.length > 0 && (
                      <>
                        {itemsWithCategoryName?.map(
                          (category: any, index: number) => (
                            <React.Fragment
                              key={category.planCategory.concat(index)}
                            >
                              {category.planCategory && (
                                <StyledCategoryContainer>
                                  <StyledListOfCategoryContainer>
                                    {category.planCategory}
                                  </StyledListOfCategoryContainer>
                                  <ContainerPlanNamesFlex
                                    aria-label={category.planCategory}
                                  >
                                    {category.planDetails.map(
                                      (plan: any, planIndex: number) => (
                                        <li
                                          key={plan.planName.concat(planIndex)}
                                          style={{ lineHeight: '18px' }}
                                        >
                                          <PlanNameLink
                                            after={
                                              <IconSymbol icon="chevron_right" />
                                            }
                                            data-testid={`guest-plan-names-${plan?.planName
                                              .replaceAll(' ', '-')
                                              .toLowerCase()}`}
                                            isStandardAnchor
                                            key={plan.planName.concat(
                                              planIndex
                                            )}
                                            onClick={() =>
                                              routeToPsxOrDualYearSelection(
                                                plan,
                                                planIndex,
                                                category.planCategory
                                              )
                                            }
                                            openNewWindow={
                                              !isDualYearCheckEnabledForGuest(
                                                lob,
                                                featureFlags,
                                                plan
                                              ) &&
                                              IsExternallink(
                                                plan,
                                                lob,
                                                featureFlags
                                              )
                                            }
                                          >
                                            {plan.planName}
                                          </PlanNameLink>
                                        </li>
                                      )
                                    )}
                                  </ContainerPlanNamesFlex>
                                </StyledCategoryContainer>
                              )}
                              <div style={{ borderTop: '1px solid #E5E5E6' }} />
                            </React.Fragment>
                          )
                        )}
                      </>
                    )}

                    {itemsWithoutCategoryName?.length > 0 && (
                      <ContainerPlanNamesFlex>
                        {itemsWithoutCategoryName.map(
                          (item: any, index: number) => (
                            <React.Fragment
                              key={
                                item.planDetails[0]?.planName.concat(index) ||
                                Math.random()
                              }
                            >
                              {item.planDetails.map(
                                (plan: any, planIndex: number) => (
                                  <li
                                    key={plan.planName.concat(planIndex)}
                                    style={{ lineHeight: '18px' }}
                                  >
                                    <PlanNameLink
                                      after={
                                        <IconSymbol icon="chevron_right" />
                                      }
                                      data-testid={`guest-plan-names-${plan?.planName
                                        .replaceAll(' ', '-')
                                        .toLowerCase()}`}
                                      isStandardAnchor
                                      key={plan.planName.concat(planIndex)}
                                      onClick={() =>
                                        routeToPsxOrDualYearSelection(
                                          plan,
                                          planIndex
                                        )
                                      }
                                      openNewWindow={
                                        !isDualYearCheckEnabledForGuest(
                                          lob,
                                          featureFlags,
                                          plan
                                        ) &&
                                        IsExternallink(plan, lob, featureFlags)
                                      }
                                    >
                                      {plan.planName}
                                    </PlanNameLink>
                                  </li>
                                )
                              )}
                            </React.Fragment>
                          )
                        )}
                      </ContainerPlanNamesFlex>
                    )}
                  </ContainerForPlanSelection>
                ) : (
                  <Flex
                    css={{ marginTop: '40px' }}
                    data-testid="no-matching-plans"
                    justify="center"
                  >
                    <Text css={StyledTextForNoResults}>{noMatchingPlans}</Text>
                  </Flex>
                )}
              </div>
            </>
          )}
        </StyledContainerforPage>
      </ContainerForMultiplePlans>
    </PageBody>
  );
};
